import type { RockStatus } from "@/modules/rocks/types";
import {
  ANNUAL_MEETING_SECTIONS,
  BUILTIN_TYPE_DEFS,
  QUARTERLY_MEETING_SECTIONS,
  WEEKLY_MEETING_SECTIONS,
  type MeetingSectionDef,
} from "@/modules/meetings/builtin-sections";

export type { MeetingSectionDef };
export {
  ANNUAL_MEETING_SECTIONS,
  QUARTERLY_MEETING_SECTIONS,
  WEEKLY_MEETING_SECTIONS,
} from "@/modules/meetings/builtin-sections";

export const BUILTIN_SECTION_KEYS = [
  'wins', 'scorecard', 'rocks', 'headlines', 'tasks', 'issues', 'conclusion',
] as const;

export type BuiltinSectionKey = (typeof BUILTIN_SECTION_KEYS)[number];

/**
 * Where in the month a monthly meeting lands. "The 15th" and "the third
 * Tuesday" are both things teams say, and neither expresses the other.
 *
 * `nth: -1` is the last such weekday in the month. There is no 5th Tuesday in
 * most months, so counting forward stops at four.
 */
export type MonthlyOn =
  | { on: 'day'; dayOfMonth: number }
  | { on: 'weekday'; nth: 1 | 2 | 3 | 4 | -1; dayOfWeek: number };

/**
 * Cadence, and only as much scheduling detail as the cadence can carry.
 *
 * Weekly and monthly name a recurring slot, so they carry a time. Quarterly and
 * annual are placed on a date the team agreed (#409 D3) — a stored time would
 * be a default for a picker nobody reaches through a series.
 */
export type Recurrence =
  | { frequency: 'weekly'; dayOfWeek: number; time: string }
  | ({ frequency: 'monthly'; time: string } & MonthlyOn)
  | { frequency: 'quarterly' }
  | { frequency: 'yearly' };

export interface MeetingType {
  /** DB uuid — absent for locally-generated defaults before first DB fetch. */
  id?: string;
  /** The team that defines this row. Resolution is per key up the team chain. */
  teamId: string;
  key: string;
  /** `null` inherits the nearest ancestor's label, then the system invariant. */
  label: string | null;
  builtIn: boolean;
  sortOrder: number;
  /** `null` inherits the nearest ancestor's agenda. `[]` is a deliberately empty agenda. */
  sections: MeetingSectionDef[] | null;
  fathomMeetingTitle?: string;
  recurrence?: Recurrence;
  notificationTimingHours: number;
  prepEmailEnabled: boolean;
  /** Late-bound, like `Meeting.invitees`: null follows team membership. */
  defaultInvitees: string[] | null;
  defaultHostId?: string;
}

const DEFAULT_MEETING_TYPE_FIELDS = {
  // These are templates, not rows: no team defines them until one is stored.
  // `''` is the same no-team value produced by the store's deserializer and
  // the effective meeting-type resolver.
  teamId: '',
  notificationTimingHours: 24,
  prepEmailEnabled: false,
  // Automatic: the team's members, resolved when the meeting is evaluated.
  defaultInvitees: null,
};

/**
 * A built-in type is the team's own meeting, so its roster follows the team.
 * A custom type is a standing 1-on-1, a finance review, a subset of the team —
 * it names its own people, starting from none rather than from everyone.
 */
export const defaultInviteesForNewType = (builtIn: boolean): string[] | null => (builtIn ? null : []);

export const BUILTIN_MEETING_TYPES: MeetingType[] = BUILTIN_TYPE_DEFS.map((t) => ({
  ...t, builtIn: true, ...DEFAULT_MEETING_TYPE_FIELDS,
}));

export const FREE_MEETING_TYPE_KEYS = new Set(['weekly', 'quarterly']);

/**
 * Types whose meetings are booked one at a time and never as a series.
 *
 * A quarterly or annual session is placed on a date the whole team agreed
 * around, not on the third Tuesday forever (#409 D3), so these carry no
 * cadence and the schedule editor offers them none.
 */
export const MANUALLY_SCHEDULED_TYPE_KEYS = new Set(['quarterly', 'yearly']);

/** How far ahead of a meeting its prep email may go out. Seven days is the ceiling. */
export const PREP_LEAD_HOURS = { min: 1, max: 168, fallback: 24 } as const;

/**
 * A typed lead time, held inside the bounds. `max` on a number input governs the
 * spinner and nothing else, so a typed 500 reaches the store unless clamped here.
 */
export function clampPrepLeadHours(value: number): number {
  if (!Number.isFinite(value) || value < PREP_LEAD_HOURS.min) return PREP_LEAD_HOURS.fallback;
  return Math.min(Math.trunc(value), PREP_LEAD_HOURS.max);
}

export interface AttendeeWin {
  personal: string;
  personalEmoji: string;
  professional: string;
  professionalEmoji: string;
}

// Weekly Meetings
export interface MeetingSectionTargets {
  wins: number;
  scorecard: number;
  rocks: number;
  tasks: number;
  issues: number;
  conclusion: number;
}

export const DEFAULT_SECTION_TARGETS: MeetingSectionTargets = {
  wins: 5,
  scorecard: 5,
  rocks: 5,
  tasks: 5,
  issues: 65,
  conclusion: 5,
};

export type MeetingSection = 'setup' | 'wins' | 'scorecard' | 'rocks' | 'tasks' | 'issues' | 'conclusion';

export const MEETING_SECTIONS: MeetingSection[] = ['setup', 'wins', 'scorecard', 'rocks', 'tasks', 'issues', 'conclusion'];

export type MeetingStatus = 'scheduled' | 'in-progress' | 'complete' | 'cancelled';

export interface MeetingAttendee {
  id: string;
  meetingId: string;
  memberId: string;
  score: number | null;
  personalWin?: string;
  personalWinEmoji?: string;
  professionalWin?: string;
  professionalWinEmoji?: string;
}
export type FathomCandidateStatus = 'pending' | 'accepted' | 'rejected' | 'replaced';

export interface FathomCandidateTask {
  id: string;
  title: string;
  status: FathomCandidateStatus;
  decidedByUserId?: string;
  decidedAt?: string;
  acceptedTaskId?: string;
  /** Set by analyze-meeting when a manual to-do closely matches this candidate. */
  matchedTaskId?: string;
  matchedTaskTitle?: string;
  /** Raw assignee captured from Fathom payload (used for owner detection). */
  assigneeName?: string;
  assigneeEmail?: string;
}

export interface FathomSummary {
  url: string;
  summary?: string;
  candidateTasks: FathomCandidateTask[];
  attachedAt: string;
  attachedByUserId?: string;
  source: 'manual' | 'webhook';
}

export interface IDSNote {
  identify?: string;
  discuss?: string;
  solve?: string;
}

export interface RockSnapshotItem {
  rockId: string;
  title: string;
  status: RockStatus;
  ownerId?: string;
}

export interface MeetingDraftState {
  activeSection: string;
  meetingElapsed: number;
  sectionElapsed: number;
  sectionElapsedMap?: Record<string, number>;
  isPaused: boolean;
  meetingIssueState: Record<string, { queuedAction: string | null; pendingTasks?: { title: string; ownerId: string; dueDate: string }[] }>;
  meetingTaskState: Record<string, { queuedAction: string | null }>;
  /** Issues created during this meeting session — drives the drawer's NEW badge. Session-local
   * (unlike createdTaskIds, not a committed Meeting field): it only needs to survive an explicit
   * "Save" of the in-progress draft, not the final end-of-meeting record. */
  createdIssueIds?: string[];
  /** Task IDs that existed when the meeting started — lets a paused-then-resumed session keep
   * diffing "created this meeting" against the original start point, not the resume point. */
  initialTaskIds?: string[];
  /** Issue id → status (or 'resolved' when `resolved: true`) at the moment the meeting started —
   * same purpose as initialTaskIds, for detecting "resolved this meeting" across a pause/resume. */
  initialIssueStatuses?: Record<string, string>;
  changedRecords?: import('./meeting-notices').ChangedRecords;
}

export interface AgendaItem {
  key: string;
  label: string;
  length: number;
  custom: boolean;
}

/**
 * A recurring series (#411). The rule is authoritative; occurrences are
 * computed from it and materialized into `meetings` rows.
 *
 * The defaults here are the series', seeded from its type at creation and
 * authoritative afterwards — a calendar event does not keep inheriting from the
 * template it was made from. `null` on the operational pair still means
 * "resolve against the type", matching the dispatcher's COALESCE, and `null`
 * invitees still means the roster follows the team.
 *
 * No duration: a meeting's length is the sum of its section durations, so a
 * stored copy would freeze it against an agenda edit.
 */
export interface MeetingSchedule {
  id: string;
  teamId: string;
  meetingType: string;
  /** Instant of the first occurrence. */
  dtstart: string;
  /** RFC 5545 rule text, UNTIL included. */
  rrule: string;
  /** Occurrences that should not exist. A deleted one is excluded, not skipped. */
  exdates: string[];
  hostId?: string;
  invitees: string[] | null;
  prepEmailEnabled: boolean | null;
  notificationTimingHours: number | null;
  /** Set when the series was ended deliberately. UNTIL in the rule stops it. */
  /** `null` clears it, which is how an ended series is restarted. */
  endedAt?: string | null;
}

export interface Meeting {
  id: string;
  /** The team this meeting belongs to. Absent means the root. */
  teamId?: string;
  year?: number;
  quarter?: number;
  date: string;
  dataPeriodWeek: number;
  hostId?: string;
  targetLengthMinutes: number;
  actualLengthMinutes?: number;
  averageScore?: number;
  reportMonthly: boolean;
  meetingType: string;
  sectionTargets: Record<string, number>;
  /** Seconds actually spent per section, written once on completion. */
  sectionElapsedMap?: Record<string, number>;
  resolvedIssueIds: string[];
  createdTaskIds: string[];
  unresolvedIssueIds?: string[];
  notes?: string;
  issueNotes?: Record<string, IDSNote>;
  rockSnapshot?: RockSnapshotItem[];
  attendees: MeetingAttendee[];
  fathomSummaries: FathomSummary[];
  meetingRecap?: string;
  status: MeetingStatus;
  sessionCount: number;
  draftState?: MeetingDraftState;
  // Scheduling fields
  scheduledAt?: string;
  meetingScheduleId?: string;
  /** The instant the schedule's rule produced. Set with `meetingScheduleId` or not at all. */
  occurrenceStart?: string;
  invitees: string[];
  notificationTimingHours?: number;
  prepEmailEnabled?: boolean | null;
  prepEmailSentAt?: string;
  agenda?: AgendaItem[];
  sectionData?: Record<string, any>;
}
